/**
 * templateMaterialization.js — story 224, DL25-a (RN-07.22): accepting a template update makes the editor show the content of the
 * target version. Pure helpers over the workflow model (`nodes`, `subWorkflow.nodes`): none of them mutates what it receives, so
 * Revert (which reloads the saved copy) and a reducer that React may run twice stay correct.
 *
 * Also the two things accepting discards (G2: pending library-operation intents of the unit's jobs) and the one deleting a unit
 * retires (G4: its pending updates, and those of the units it contains).
 */
import { containsUnit, unitIdOf } from './templateLinks'

/** @returns {object[]} every node of a level and of the loaded levels below it (a subworkflow not loaded has no `nodes`) */
export const flattenNodes = (nodes = []) => nodes.flatMap((node) => [node, ...flattenNodes(node?.subWorkflow?.nodes || [])])

/** @returns {object} the node `nodeId` at any loaded depth, or `{}` */
export const findNodeDeep = (nodes = [], nodeId = '') => flattenNodes(nodes).find((node) => node.id === nodeId) || {}
const findNode = findNodeDeep

const hasPendingOperationIntent = (node) => Boolean(node?.operationLink?.intent) && node.operationLink.intent !== 'KEEP'

/** The nodes a unit holds: the whole process for the ROOT, the loaded content of its subworkflow node otherwise. */
const unitNodesOf = (workflow = {}, unit = {}) => unit.scope === 'ROOT'
  ? workflow.nodes || []
  : findNode(workflow.nodes || [], unit.subworkflowNodeId).subWorkflow?.nodes || []

/**
 * G2 (S158, S161): the codes of the jobs of a unit (the whole workflow for the ROOT, nested subworkflows included) that carry a
 * pending library-operation intent (#130): accepting the update replaces them, so those intents are discarded. `[]` when none.
 * @param {object} workflow the editor's workflow
 * @param {object} unit the link (or intent) of the unit
 * @returns {string[]}
 */
export const discardedOperationJobCodes = (workflow = {}, unit = {}) =>
  flattenNodes(unitNodesOf(workflow, unit)).filter((node) => node.nodeType === 'JOB' && hasPendingOperationIntent(node)).map((node) => node.code)

/** Replaces the node `nodeId` at any loaded depth with `replace(node)`, copying only the path to it. */
const replaceNode = (nodes, nodeId, replace) => {
  const walk = (level) => level.map((node) => {
    if (node.id === nodeId) {
      return replace(node)
    }
    const inner = node.subWorkflow?.nodes || []
    if (inner.length === 0) {
      return node
    }
    const walked = walk(inner)
    return walked.some((child, index) => child !== inner[index]) ? { ...node, subWorkflow: { ...node.subWorkflow, nodes: walked } } : node
  })
  return walk(nodes)
}

/**
 * DL25-a: what the editor shows once the update of a unit is accepted. `target` is the loaded template version
 * (`GET workflow/{targetTemplateWorkflowId}`).
 * - SUBWORKFLOW: the node keeps its id and its `subWorkflow` becomes the target (what inserting it would load).
 * - ROOT: the whole process (nodes, positions, subworkflow ids) becomes the target's; the workflow's own data, its BOM header and
 *   its documents are untouched (RN-03.7, DL26), and the pending #117 BOM link is dropped (G3: it is not written).
 * The units and jobs of the replaced content leave the editor with their intents and baselines: the result says which.
 * @param {object} workflow the editor's workflow (not mutated)
 * @param {object} intent the accepted update (`templateUpdateIntent`)
 * @param {object} target the loaded target version
 * @returns {{workflow: object, found: boolean, vanishedNodeIds: string[], vanishedUnitIds: string[]}} `found` false (the unit is not
 * among the loaded levels) leaves `workflow` as it was
 */
export const materializeTemplateUpdate = (workflow = {}, intent = {}, target = {}) => {
  if (intent.scope === 'ROOT') {
    const { pendingBomLink, ...rest } = workflow
    const vanished = flattenNodes(workflow.nodes || [])
    return {
      workflow: { ...rest, nodes: target.nodes || [], workflowPositions: target.workflowPositions || [], subWorkflowIds: target.subWorkflowIds || [] },
      found: true,
      vanishedNodeIds: vanished.map((node) => node.id),
      vanishedUnitIds: vanished.filter((node) => node.nodeType === 'WORKFLOW').map((node) => node.id)
    }
  }
  const unit = findNode(workflow.nodes || [], intent.subworkflowNodeId)
  if (!unit.id) {
    return { workflow, found: false, vanishedNodeIds: [], vanishedUnitIds: [] }
  }
  const vanished = flattenNodes(unit.subWorkflow?.nodes || [])
  const replaced = replaceNode(workflow.nodes || [], unit.id, (node) => ({ ...node, subWorkflow: target }))
  return {
    workflow: { ...workflow, nodes: replaced },
    found: true,
    vanishedNodeIds: vanished.map((node) => node.id),
    vanishedUnitIds: vanished.filter((node) => node.nodeType === 'WORKFLOW').map((node) => node.id)
  }
}

/**
 * G4 (S160): deleting a node of the canvas retires the pending update of its unit and of every unit it contains, also when the
 * deleted node had no update of its own. A unit is inside when its chain of `parentUnitNodeId` reaches the node (E1), when its
 * top-level node is the deleted one (a container with no link of its own), or when it is in the loaded tree of the deleted node.
 * Anything that is not a subworkflow node retires nothing.
 * @param {object[]} pending the pending updates
 * @param {object} deletedNode the node removed from the workflow
 * @param {object[]} links the `TemplateLinkView[]` of E1, to follow the chains of parents
 * @returns {object[]} `pending` itself when nothing is retired
 */
export const withoutUpdatesOfDeletedNode = (pending = [], deletedNode = {}, links = []) => {
  if (deletedNode.nodeType !== 'WORKFLOW') {
    return pending
  }
  const outer = { scope: 'SUBWORKFLOW', subworkflowNodeId: deletedNode.id }
  const loadedUnitIds = new Set(flattenNodes(deletedNode.subWorkflow?.nodes || []).filter((node) => node.nodeType === 'WORKFLOW').map((node) => node.id))
  const kept = pending.filter((intent) => !(
    unitIdOf(intent) === deletedNode.id ||
    intent.topLevelNodeId === deletedNode.id ||
    loadedUnitIds.has(unitIdOf(intent)) ||
    containsUnit(outer, intent, links)
  ))
  return kept.length === pending.length ? pending : kept
}

/**
 * G4 / H15 inside a SubworkflowDialog: the subworkflow nodes the dialog showed when it opened (`before`) that the accepted content
 * no longer has. Each one retires its own pending updates and decisions, and those of the units it contained (`withoutUpdatesOfDeletedNode`).
 * @param {object[]} before the nodes of the unit as the dialog opened
 * @param {object[]} after the nodes of the unit as the dialog accepts it
 * @returns {object[]} the deleted nodes of type WORKFLOW, at any loaded depth
 */
export const deletedUnitNodes = (before = [], after = []) => {
  const remaining = new Set(flattenNodes(after).map((node) => node.id))
  return flattenNodes(before).filter((node) => node.nodeType === 'WORKFLOW' && !remaining.has(node.id))
}
